import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { addDays, money, today } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChargesSection, chargeAmount } from "./ChargesSection";
import { Field } from "@/pages/crm/LeadForm";
import { DOC_CONFIG, PURPOSES } from "./config";

const emptyItem = () => ({ product_id: "", name: "", description: "", qty: 1, price: 0, tax_rate: 18 });

export const DocumentForm = ({ kind, open, onOpenChange, doc, presetLeadId, onSaved }) => {
  const cfg = DOC_CONFIG[kind];
  const editing = !!doc?.id;
  const [form, setForm] = useState(null);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [p, s, l, st] = await Promise.all([
        api.get("/products"),
        cfg.party === "supplier" ? api.get("/suppliers") : Promise.resolve({ data: [] }),
        api.get("/leads", { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
        api.get("/settings"),
      ]);
      setProducts(p.data); setSuppliers(s.data); setLeads(l.data.items || []); setSettings(st.data);

      const base = {
        lead_id: presetLeadId || "", customer_name: "", company: "", email: "", phone: "",
        address: "", gstin: "", date: today(), valid_until: addDays(15), due_date: addDays(kind === "invoices" ? 7 : 15),
        expected_delivery: addDays(10), items: [emptyItem()], charges: [], discount: 0, notes: "",
        terms: (st.data.terms || {})[kind === "purchase-orders" ? "purchase_order" : kind.replace(/s$/, "")] || "",
        status: "Draft", supplier_id: "", supplier_name: "", purpose: "Stock Replenishment",
        payment_terms: "50% advance, balance before dispatch",
      };
      // Reorder hand-off from Inventory -> pre-fills supplier and product lines
      if (!doc && kind === "purchase-orders") {
        try {
          const raw = sessionStorage.getItem("mv_po_draft");
          if (raw) {
            const draft = JSON.parse(raw);
            sessionStorage.removeItem("mv_po_draft");
            const sup = (s.data || []).find((x) => x.id === draft.supplier_id);
            Object.assign(base, {
              supplier_id: draft.supplier_id || "",
              supplier_name: draft.supplier_name || "",
              purpose: "Stock Replenishment",
              items: draft.items?.length ? draft.items : [emptyItem()],
              notes: "Raised from low stock reorder suggestions.",
              ...(sup ? { email: sup.email, phone: sup.phone, address: sup.address, gstin: sup.gstin,
                          payment_terms: sup.payment_terms || base.payment_terms } : {}),
            });
          }
        } catch { /* ignore */ }
      }

      if (doc) setForm({ ...base, ...doc, items: doc.items?.length ? doc.items : [emptyItem()], charges: doc.charges || [] });
      else {
        if (presetLeadId) {
          const lead = (l.data.items || []).find((x) => x.id === presetLeadId);
          if (lead) Object.assign(base, {
            customer_name: lead.customer_name, company: lead.company, email: lead.email,
            phone: lead.phone, address: lead.address, lead_id: lead.id,
          });
        }
        setForm(base);
      }
    })();
    // eslint-disable-next-line
  }, [open, doc, presetLeadId, kind]);

  const totals = useMemo(() => {
    if (!form) return { sub: 0, tax: 0, charges: 0, total: 0 };
    const sub = form.items.reduce((a, i) => a + Number(i.qty || 0) * Number(i.price || 0), 0);
    let tax = form.items.reduce((a, i) => a + Number(i.qty || 0) * Number(i.price || 0) * (Number(i.tax_rate || 0) / 100), 0);
    let charges = 0;
    (form.charges || []).forEach((c) => {
      const amount = chargeAmount(c, sub);
      charges += amount;
      if (c.taxable !== false) tax += (amount * Number(c.tax_rate || 0)) / 100;
    });
    return { sub, tax, charges, total: Math.max(0, sub + charges + tax - Number(form.discount || 0)) };
  }, [form]);

  if (!form) return null;

  const setItem = (idx, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  const pickProduct = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setItem(idx, {
      product_id: p.id, name: p.name, description: p.description,
      price: kind === "purchase-orders" ? p.purchase_cost : p.selling_price,
      tax_rate: p.tax_rate,
    });
  };

  const pickLead = (lid) => {
    const lead = leads.find((l) => l.id === lid);
    if (!lead) return setForm({ ...form, lead_id: "" });
    setForm({
      ...form, lead_id: lead.id, customer_name: lead.customer_name, company: lead.company,
      email: lead.email, phone: lead.phone, address: lead.address,
    });
  };

  const pickSupplier = (sid) => {
    const s = suppliers.find((x) => x.id === sid);
    if (!s) return;
    setForm({
      ...form, supplier_id: s.id, supplier_name: s.name, email: s.email, phone: s.phone,
      address: s.address, gstin: s.gstin, payment_terms: s.payment_terms || form.payment_terms,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.items.some((i) => i.name)) return toast.error("Add at least one product line.");
    setBusy(true);
    const payload = {
      ...form,
      discount: Number(form.discount || 0),
      charges: (form.charges || []).filter((c) => c.name).map((c) => ({
        ...c, value: Number(c.value || 0), tax_rate: Number(c.tax_rate || 0),
        cost_amount: Number(c.cost_amount || 0),
      })),
      items: form.items.filter((i) => i.name).map((i) => ({
        ...i, qty: Number(i.qty || 0), price: Number(i.price || 0), tax_rate: Number(i.tax_rate || 0),
      })),
    };
    ["id", "number", "created_by", "created_by_name", "created_at", "updated_at", "subtotal",
      "tax_total", "total", "charges_total", "paid_amount", "attachments", "kind", "assigned_to",
      "source_doc"]
      .forEach((k) => delete payload[k]);
    try {
      let res;
      if (editing) await api.patch(`/documents/${kind}/${doc.id}`, payload);
      else res = await api.post(`/documents/${kind}`, payload);
      toast.success(`${cfg.singular} ${editing ? "updated" : "created"}`);
      const so = res?.data?.stock_out;
      if (so?.lines) {
        toast.info(`Stock issued for ${so.lines} product line${so.lines > 1 ? "s" : ""}`);
      }
      if (so?.created_products?.length) {
        toast.warning(`Not in the catalogue, created with negative stock: ${so.created_products.join(", ")}`);
      } else if (so?.negative?.length) {
        toast.warning(`Negative stock now: ${so.negative.map((n) => `${n.name} (${n.qty})`).join(", ")}`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? `Edit ${doc.number}` : `New ${cfg.singular.toLowerCase()}`}
          </DialogTitle>
          <DialogDescription>
            Pick a product preset and the price, tax and description fill in automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6">
          {/* Party */}
          <section className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {cfg.party === "supplier" ? "Supplier" : "Customer"}
            </p>
            {cfg.party === "supplier" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Supplier" required>
                  <Select value={form.supplier_id} onValueChange={pickSupplier}>
                    <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Purchase purpose">
                  <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Related lead or customer">
                  <Select value={form.lead_id || "none"} onValueChange={(v) => (v === "none" ? setForm({ ...form, lead_id: "" }) : pickLead(v))}>
                    <SelectTrigger><SelectValue placeholder="Not linked" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not linked</SelectItem>
                      {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.customer_name} . {l.company}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Pick from your leads / customers">
                  <Select value={form.lead_id || "none"} onValueChange={(v) => (v === "none" ? setForm({ ...form, lead_id: "" }) : pickLead(v))}>
                    <SelectTrigger data-testid="doc-lead"><SelectValue placeholder="Select or type manually" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Enter manually</SelectItem>
                      {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.customer_name} . {l.company}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Customer name" required>
                  <Input required value={form.customer_name} data-testid="doc-customer"
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                </Field>
                <Field label="Company"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
                <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="GSTIN"><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></Field>
              </div>
            )}
            <Field label="Billing address">
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
          </section>

          {/* Dates */}
          <section className="grid gap-4 sm:grid-cols-3">
            <Field label="Document date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            {cfg.hasValidUntil && <Field label="Valid until"><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></Field>}
            {cfg.hasDueDate && <Field label="Payment due date" hint={kind === "invoices" ? "Defaults to 7 days. You can change it." : undefined}><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>}
            {cfg.hasExpectedDelivery && <Field label="Expected delivery"><Input type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} /></Field>}
            <Field label="Payment terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></Field>
          </section>

          {/* Items */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Products</p>
              <Button type="button" size="sm" variant="soft" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} data-testid="add-item">
                <Plus className="h-3.5 w-3.5" /> Add line
              </Button>
            </div>

            <div className="space-y-3">
              {form.items.map((it, idx) => (
                <div key={idx} className="grid gap-3 rounded-xl border border-border bg-muted/30 p-3 sm:grid-cols-12">
                  <div className="sm:col-span-5">
                    <Field label="Product">
                      <Select value={it.product_id} onValueChange={(v) => pickProduct(idx, v)}>
                        <SelectTrigger data-testid={`item-product-${idx}`}><SelectValue placeholder="Choose preset" /></SelectTrigger>
                        <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Input className="mt-2" placeholder="Or type a description" value={it.name}
                      onChange={(e) => setItem(idx, { name: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Qty"><Input type="number" min="0" step="1" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} /></Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Rate"><Input type="number" min="0" value={it.price} onChange={(e) => setItem(idx, { price: e.target.value })} /></Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Tax %">
                      <Select value={String(it.tax_rate)} onValueChange={(v) => setItem(idx, { tax_rate: Number(v) })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{(settings.tax_rates || [0, 5, 12, 18, 28]).map((t) => <SelectItem key={t} value={String(t)}>{t}%</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="flex items-end sm:col-span-1">
                    <Button type="button" variant="ghost" size="iconSm" className="text-destructive"
                      disabled={form.items.length === 1}
                      onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <ChargesSection kind={kind} form={form} setForm={setForm} subtotal={totals.sub}
            taxRates={settings.tax_rates || [0, 5, 12, 18, 28]} />

          {/* Totals */}
          <section className="grid gap-4 sm:grid-cols-2">
            <Field label="Discount amount (INR)" hint="Leave as 0 and the discount row will not appear on the document.">
              <Input type="number" min="0" value={form.discount} data-testid="doc-discount"
                onChange={(e) => setForm({ ...form, discount: e.target.value })} />
            </Field>
            <div className="rounded-xl border border-border bg-card p-4">
              <Row label="Subtotal" value={money(totals.sub)} testId="totals-subtotal" />
              {totals.charges > 0 && (
                <Row label="Additional charges" value={money(totals.charges)} testId="totals-charges" />
              )}
              {Number(form.discount) > 0 && (
                <Row label="Discount" value={`- ${money(form.discount)}`} tone="text-destructive" testId="totals-discount" />
              )}
              <Row label="Tax" value={money(totals.tax)} testId="totals-tax" />
              <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                <span className="text-sm font-semibold">Grand total</span>
                <span className="num text-lg font-bold text-primary" data-testid="totals-grand">{money(totals.total)}</span>
              </div>
            </div>
          </section>

          <Field label="Terms and conditions">
            <Textarea rows={4} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
          </Field>
          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="doc-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : `Create ${cfg.singular.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, tone = "", testId }) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`num font-medium ${tone}`} data-testid={testId}>{value}</span>
  </div>
);
